/**
 * Color chart popup
 *
 * Fills the product page's "Color Chart" modal (snippets/color-chart-popup.liquid)
 * with the full colour-chart page. Opening and closing is handled by the theme's
 * modal-opener / modal-dialog; this only loads the content.
 *
 * Load time:
 * - The page's HTML is fetched in the background as soon as the row scrolls
 *   near the viewport, or on hover or focus, so the chart is usually ready
 *   before the popup opens. The prefetch only fills the cache. The chart goes
 *   into the popup when it opens, so no chart images download before that.
 * - Only the page's <main> is parsed, and only its sections are kept. Their
 *   images are lazy, so an open popup only downloads the swatches scrolled to.
 * - The result is shared by every popup on the page and kept in sessionStorage
 *   for 30 minutes, so other products with the same chart open instantly. The
 *   theme editor always fetches fresh.
 * - A request that takes longer than 20 seconds is abandoned and the popup shows
 *   a link to the chart page instead. The next open tries again.
 */
if (!customElements.get('color-chart-popup')) {
  const CACHE_PREFIX = 'color-chart-popup:v2:';
  const CACHE_TTL = 30 * 60 * 1000;
  const FETCH_TIMEOUT = 20000;
  const PREFETCH_MARGIN = '600px 0px';

  /** @type {Map<string, Promise<string>>} In-flight and finished loads, keyed by URL. */
  const loads = new Map();

  /**
   * Returns the sessionStorage key for a chart URL. The theme id keeps preview
   * and live themes apart.
   * @param {string} url - Chart URL.
   * @returns {string}
   */
  const cacheKey = (url) => `${CACHE_PREFIX}${window.Shopify?.theme?.id || ''}:${url}`;

  /**
   * Returns the cached chart markup, if it is still fresh.
   * @param {string} url - Chart URL.
   * @returns {?string}
   */
  const readCache = (url) => {
    if (window.Shopify?.designMode) return null;
    try {
      const entry = JSON.parse(sessionStorage.getItem(cacheKey(url)));
      if (entry && typeof entry.html === 'string' && Date.now() - entry.time < CACHE_TTL) return entry.html;
    } catch (error) {
      // Storage unavailable or entry unreadable: fetch instead.
    }
    return null;
  };

  /**
   * Caches chart markup for the rest of the session.
   * @param {string} url - Chart URL.
   * @param {string} html - Chart markup.
   */
  const writeCache = (url, html) => {
    if (window.Shopify?.designMode) return;
    try {
      sessionStorage.setItem(cacheKey(url), JSON.stringify({ time: Date.now(), html }));
    } catch (error) {
      // Storage full or blocked: the in-memory copy still works for this page.
    }
  };

  /**
   * Readies a fetched section for the product page. It removes the section's own
   * stylesheet links and scripts (the snippet already loads color-chart.css) and
   * its id, makes every image lazy (the chart page leaves its first sections'
   * images eager), and turns the page h1 into an h2, because the product title
   * is this page's h1.
   * @param {Element} section - Section from the fetched page.
   * @returns {string} Section markup.
   */
  const prepareSection = (section) => {
    section.removeAttribute('id');
    section.querySelectorAll('link[rel="stylesheet"], script').forEach((el) => el.remove());
    section.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    });
    section.querySelectorAll('h1').forEach((h1) => {
      const h2 = section.ownerDocument.createElement('h2');
      h2.className = h1.className;
      h2.innerHTML = h1.innerHTML;
      h1.replaceWith(h2);
    });
    return section.outerHTML;
  };

  /**
   * Pulls the top-level sections out of a full page's HTML. Only the <main>
   * element is parsed, which skips the header, footer and app markup.
   * @param {string} html - Full page HTML.
   * @returns {string} Markup of the sections, in page order.
   */
  const extractSections = (html) => {
    const start = html.search(/<main\b[^>]*\bid=["']?content["'\s>]/i);
    const end = html.lastIndexOf('</main>');
    const markup = start !== -1 && end > start ? html.slice(start, end + 7) : html;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const root = doc.getElementById('content') || doc.body;
    const sections = [...root.querySelectorAll('.shopify-section')]
      .filter((section) => !section.parentElement.closest('.shopify-section'));
    if (!sections.length) throw new Error('No sections found on the chart page');

    return sections.map(prepareSection).join('');
  };

  /**
   * Fetches a chart page and returns its sections' markup.
   * @param {string} url - Chart URL.
   * @returns {Promise<string>}
   */
  const fetchChart = async (url) => {
    const cached = readCache(url);
    if (cached) return cached;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = extractSections(await response.text());
      writeCache(url, html);
      return html;
    } finally {
      clearTimeout(timer);
    }
  };

  /**
   * Loads a chart once per page, however many popups or events ask for it. A
   * failed load is forgotten so the next request retries.
   * @param {string} url - Chart URL.
   * @returns {Promise<string>}
   */
  const loadChart = (url) => {
    if (!loads.has(url)) {
      loads.set(url, fetchChart(url).catch((error) => {
        loads.delete(url);
        throw error;
      }));
    }
    return loads.get(url);
  };

  class ColorChartPopup extends HTMLElement {
    connectedCallback() {
      this.url = this.dataset.url;
      this.trigger = this.querySelector('.js-color-chart-trigger');
      this.content = this.querySelector('.js-color-chart-content');
      if (!this.trigger || !this.content) return;

      if (!this.url) {
        this.setStatus('error');
        return;
      }

      this.prefetchHandler = () => this.prefetch();
      this.loadHandler = () => this.load();
      ColorChartPopup.prefetchEvents.forEach((type) => {
        this.trigger.addEventListener(type, this.prefetchHandler, { passive: true });
      });
      this.trigger.addEventListener('click', this.loadHandler);
      this.observePrefetch();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (!this.loadHandler) return;
      ColorChartPopup.prefetchEvents.forEach((type) => {
        this.trigger.removeEventListener(type, this.prefetchHandler);
      });
      this.trigger.removeEventListener('click', this.loadHandler);
    }

    /**
     * Prefetches in the background once the row comes near the viewport,
     * unless the visitor has asked to save data.
     */
    observePrefetch() {
      if (navigator.connection?.saveData || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => this.prefetch(), { timeout: 2000 });
        } else {
          setTimeout(() => this.prefetch(), 200);
        }
      }, { rootMargin: PREFETCH_MARGIN });
      this.observer.observe(this.trigger);
    }

    /**
     * Fetches the chart into the cache without touching the page, so none of
     * its images download until the popup opens. A failure is retried on open.
     */
    prefetch() {
      if (!this.loaded) loadChart(this.url).catch(() => {});
    }

    /**
     * Puts the chart into the modal, once. Runs when the popup opens; after a
     * prefetch the markup is already cached, so this is immediate.
     * @returns {Promise<void>|undefined}
     */
    load() {
      if (this.loaded || this.pending) return this.pending;

      this.setStatus('loading');
      this.pending = loadChart(this.url)
        .then((html) => {
          this.content.innerHTML = html;
          this.content.removeAttribute('aria-busy');
          this.loaded = true;
        })
        .catch((error) => {
          console.error('Color chart popup:', error);
          this.setStatus('error');
        })
        .finally(() => {
          this.pending = null;
        });
      return this.pending;
    }

    /**
     * Shows the loading or error message.
     * @param {string} status - 'loading' or 'error'.
     */
    setStatus(status) {
      const loadingEl = this.content.querySelector('.js-color-chart-loading');
      const errorEl = this.content.querySelector('.js-color-chart-error');
      if (loadingEl) loadingEl.hidden = status !== 'loading';
      if (errorEl) errorEl.hidden = status !== 'error';
      this.content.setAttribute('aria-busy', String(status === 'loading'));
    }
  }

  ColorChartPopup.prefetchEvents = ['pointerenter', 'touchstart', 'focus'];

  customElements.define('color-chart-popup', ColorChartPopup);
}
